/** Hostnames allowed for pinned venue links (Google Maps only). */
const ALLOWED_HOST_SUFFIXES = [
  "maps.google.com",
  "google.com",
  "maps.app.goo.gl",
];

function normalizeUrlInput(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function hostIsAllowed(hostname) {
  const host = (hostname || "").toLowerCase();
  return ALLOWED_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`),
  );
}

function looksLikeMapsPath(urlObj) {
  const host = urlObj.hostname.toLowerCase();
  const path = urlObj.pathname.toLowerCase();

  if (host === "maps.app.goo.gl") {
    return path.length > 1;
  }

  if (path.includes("/maps") || path.includes("/place") || path.startsWith("/@")) {
    return true;
  }

  const params = urlObj.searchParams;
  return params.has("q") || params.has("query") || params.has("ll") || params.has("pb");
}

/**
 * @param {string} raw
 * @returns {string|null} User-facing error, or null if valid
 */
export function getGoogleMapsUrlValidationError(raw) {
  const input = (raw || "").trim();
  if (!input) {
    return "Paste a Google Maps link you copied from Google Maps.";
  }

  if (input.length > 500) {
    return "Map link is too long (max 500 characters).";
  }

  if (/^(javascript|data|vbscript|file):/i.test(input)) {
    return "Only secure Google Maps links are allowed.";
  }

  let urlObj;
  try {
    urlObj = new URL(normalizeUrlInput(input));
  } catch {
    return "Enter a valid Google Maps URL (https://…).";
  }

  if (urlObj.protocol !== "https:") {
    return "Map link must use HTTPS.";
  }

  if (!hostIsAllowed(urlObj.hostname)) {
    return "Link must be from Google Maps (maps.google.com, google.com/maps, or maps.app.goo.gl).";
  }

  if (!looksLikeMapsPath(urlObj)) {
    return "That does not look like a Google Maps location link.";
  }

  return null;
}

/**
 * @param {string} raw
 * @returns {boolean}
 */
export function isValidGoogleMapsUrl(raw) {
  return getGoogleMapsUrlValidationError(raw) === null;
}

/**
 * @param {string} raw
 * @returns {string|null}
 */
export function sanitizeGoogleMapsUrl(raw) {
  if (getGoogleMapsUrlValidationError(raw)) return null;
  try {
    const urlObj = new URL(normalizeUrlInput(raw));
    urlObj.protocol = "https:";
    return urlObj.toString();
  } catch {
    return null;
  }
}

/**
 * @param {string} query
 * @returns {string}
 */
export function buildGoogleMapsSearchUrl(query) {
  const q = (query || "").trim() || "Kenya";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

/**
 * One-shot GPS snapshot as a Google Maps pin URL (same field the order API stores).
 * @param {number} latitude
 * @param {number} longitude
 * @returns {string}
 */
export function buildGoogleMapsCoordsUrl(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

/**
 * Resolve pin value on form submit.
 * @param {{ editorOpen: boolean, draft: string, savedUrl: string }} opts
 * @returns {{ value: string|null, error: string|null }}
 */
export function resolvePinLocationForSave({ editorOpen, draft, savedUrl }) {
  if (!editorOpen) {
    const existing = (savedUrl || "").trim();
    if (!existing) return { value: null, error: null };
    const existingError = getGoogleMapsUrlValidationError(existing);
    if (existingError) {
      return {
        value: null,
        error: `${existingError} Use “Update Location” to replace it.`,
      };
    }
    return { value: sanitizeGoogleMapsUrl(existing), error: null };
  }

  const trimmed = (draft || "").trim();
  if (!trimmed) {
    return {
      value: null,
      error: "Paste the Google Maps link you copied before saving.",
    };
  }

  const validationError = getGoogleMapsUrlValidationError(trimmed);
  if (validationError) {
    return { value: null, error: validationError };
  }

  return { value: sanitizeGoogleMapsUrl(trimmed), error: null };
}
